import React from 'react';
import { ChevronDown, ChevronRight, RefreshCw, LineChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

const ALL_PROJECTS_FOR_DROPDOWN = [
  { id: 'dream_kokan', name: 'Dream Kokan City', type: 'land' },
  { id: 'dream_nagpur', name: 'Dream Nagpur City', type: 'land' },
  { id: 'brahma_heights', name: 'Brahma Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'godavari_heights', name: 'Godavari Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'siddhivinayak_heights', name: 'Siddhivinayak Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'kaveri_heights', name: 'Kaveri Heights (Siddhivinayak Infra)', type: 'flat', parent: 'siddhivinayak_infra' },
  { id: 'dream_riviera_a', name: "Dream Riviera 'A' Wing", type: 'flat' },
  { id: 'dream_riviera_b', name: "Dream Riviera 'B' Wing", type: 'flat' },
  { id: 'sai_plaza', name: 'Sai Plaza', type: 'flat' }
];

const TREND_SERIES = [
  { key: 'totalLead', label: 'Leads', color: '#3b82f6' },
  { key: 'totalOfficeVisit', label: 'Office Visits', color: '#06b6d4' },
  { key: 'totalProjectVisit', label: 'Project Visits', color: '#8b5cf6' },
  { key: 'totalScopeOfCustomer', label: 'Scope', color: '#f59e0b' },
  { key: 'totalBooking', label: 'Bookings', color: '#14b8a6' },
  { key: 'totalConfirmSale', label: 'Confirm Sale', color: '#10b981' },
  { key: 'totalRegistry', label: 'Registry', color: '#6366f1' },
  { key: 'totalPossession', label: 'Possession', color: '#ec4899' },
];

const CustomLegend = () => (
  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-2 px-2" data-testid="trend-custom-legend">
    {TREND_SERIES.map(s => (
      <div key={s.key} className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: s.color }} />
        <span className="text-xs font-medium text-slate-700">{s.label}</span>
      </div>
    ))}
  </div>
);

const TrendTab = ({
  yearMonthTrend,
  selectedTrendProject,
  trendYear, setTrendYear,
  expandedYears, setExpandedYears,
  expandedMonths, setExpandedMonths,
  monthDayData, setMonthDayData,
  API
}) => {
  const toggleYear = (year) => setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));

  const toggleMonthExpand = async (year, monthNum) => {
    const key = `${year}-${monthNum}`;
    const willOpen = !expandedMonths[key];
    setExpandedMonths(prev => ({ ...prev, [key]: willOpen }));
    if (willOpen && monthDayData[key] === undefined) {
      try {
        const projectParam = selectedTrendProject || 'all';
        const res = await fetch(`${API}/marketing-dashboard/stats/daily-trend?year=${year}&month=${monthNum}&project=${projectParam}`);
        const data = await res.json();
        setMonthDayData(prev => ({ ...prev, [key]: data.days || [] }));
      } catch (err) {
        setMonthDayData(prev => ({ ...prev, [key]: [] }));
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LineChart className="w-5 h-5 text-blue-600" />
          {selectedTrendProject === 'all'
            ? 'ALL PROJECTS'
            : (ALL_PROJECTS_FOR_DROPDOWN.find(p => p.id === selectedTrendProject)?.name.replace(' (Siddhivinayak Infra)', '').toUpperCase() || selectedTrendProject.toUpperCase())} - Year/Month Trend Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Yearly Trend Bar Chart */}
        {yearMonthTrend.length > 0 && (
          <>
            <div className="mb-8">
              <h3 className="font-bold text-sm text-slate-700 mb-3">Yearly Trend Overview</h3>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={yearMonthTrend} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="year" fontSize={12} fontWeight={600} tick={{ fill: '#334155' }} />
                  <YAxis fontSize={10} tick={{ fill: '#64748b' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  {TREND_SERIES.map(s => (
                    <Bar isAnimationActive={false} key={s.key} dataKey={s.key} fill={s.color} name={s.label} radius={[4,4,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend />
            </div>

            {/* Monthly Trend Bar Chart */}
            <div className="mb-8">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <h3 className="font-bold text-sm text-slate-700">
                  Monthly Trend{trendYear ? ` — ${trendYear}` : ''}
                </h3>
                <div className="flex flex-wrap gap-1.5" data-testid="monthly-trend-year-buttons">
                  {yearMonthTrend.map(y => (
                    <Button
                      key={y.year}
                      size="sm"
                      variant={trendYear === y.year ? 'default' : 'outline'}
                      className={trendYear === y.year ? 'bg-blue-600 hover:bg-blue-700 text-white h-8' : 'h-8'}
                      onClick={() => setTrendYear(y.year)}
                      data-testid={`monthly-trend-year-${y.year}`}
                    >
                      {y.year}
                    </Button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={(() => {
                    const ALL_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                    const targetYear = yearMonthTrend.find(y => y.year === trendYear) || yearMonthTrend[yearMonthTrend.length - 1];
                    if (!targetYear) return [];
                    const byMonth = Object.fromEntries((targetYear.months || []).map(m => [m.month, m]));
                    return ALL_MONTHS.map(mn => byMonth[mn] || {
                      month: mn, totalLead: 0, totalOfficeVisit: 0, totalProjectVisit: 0,
                      totalScopeOfCustomer: 0, totalBooking: 0, totalConfirmSale: 0, totalRegistry: 0, totalPossession: 0,
                    });
                  })()}
                  barCategoryGap="18%"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" fontSize={11} fontWeight={600} tick={{ fill: '#334155' }} />
                  <YAxis fontSize={10} tick={{ fill: '#64748b' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip formatter={(value) => value.toLocaleString()} />
                  {TREND_SERIES.map(s => (
                    <Bar isAnimationActive={false} key={s.key} dataKey={s.key} fill={s.color} name={s.label} radius={[3,3,0,0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend />
            </div>
          </>
        )}

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border" data-testid="trend-data-table">
            <thead>
              <tr className="bg-blue-800 text-white">
                <th className="px-3 py-2 text-left border">YEAR/MONTH</th>
                <th className="px-3 py-2 text-right border">LEADS</th>
                <th className="px-3 py-2 text-right border">OFFICE VISITS</th>
                <th className="px-3 py-2 text-right border">PROJECT VISITS</th>
                <th className="px-3 py-2 text-right border">SCOPE</th>
                <th className="px-3 py-2 text-right border">BOOKINGS</th>
                <th className="px-3 py-2 text-right border">CONFIRM SALE</th>
                <th className="px-3 py-2 text-right border">REGISTRY</th>
                <th className="px-3 py-2 text-right border">POSSESSION</th>
                <th className="px-3 py-2 text-right border bg-blue-900">CONVERSION %</th>
              </tr>
            </thead>
            <tbody>
              {yearMonthTrend.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-stone-500">
                    No data available for {selectedTrendProject === 'all' ? 'All Projects' : (ALL_PROJECTS_FOR_DROPDOWN.find(p => p.id === selectedTrendProject)?.name || selectedTrendProject)}
                  </td>
                </tr>
              ) : (
                <>
                  {/* Grand Total Row */}
                  {(() => {
                    const sum = (k) => yearMonthTrend.reduce((s, y) => s + (y[k] || 0), 0);
                    const gtLead = sum('totalLead');
                    const gtBooking = sum('totalBooking');
                    const gtPct = gtLead > 0 ? ((gtBooking / gtLead) * 100).toFixed(2) : '0.00';
                    return (
                      <tr className="bg-slate-900 text-white font-bold">
                        <td className="px-3 py-2 border">GRAND TOTAL</td>
                        <td className="px-3 py-2 border text-right">{gtLead.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{sum('totalOfficeVisit').toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{sum('totalProjectVisit').toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{sum('totalScopeOfCustomer').toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{gtBooking.toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{sum('totalConfirmSale').toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{sum('totalRegistry').toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right">{sum('totalPossession').toLocaleString()}</td>
                        <td className="px-3 py-2 border text-right text-yellow-300 font-black">{gtPct}%</td>
                      </tr>
                    );
                  })()}
                  {yearMonthTrend.map((yearData) => {
                    const yearPct = yearData.totalLead > 0 ? ((yearData.totalBooking / yearData.totalLead) * 100).toFixed(2) : '0.00';
                    return (
                      <React.Fragment key={yearData.year}>
                        <tr className="bg-blue-100 cursor-pointer hover:bg-blue-200" onClick={() => toggleYear(yearData.year)}>
                          <td className="px-3 py-2 border font-bold text-blue-800 flex items-center gap-2">
                            {expandedYears[yearData.year] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            {yearData.year}
                          </td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{yearData.totalLead?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{yearData.totalOfficeVisit?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{yearData.totalProjectVisit?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{yearData.totalScopeOfCustomer?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-emerald-800">{yearData.totalBooking?.toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{(yearData.totalConfirmSale || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{(yearData.totalRegistry || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-800">{(yearData.totalPossession || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 border text-right font-bold text-blue-900 bg-blue-50">{yearPct}%</td>
                        </tr>
                        {expandedYears[yearData.year] && yearData.months?.map((month, mIdx) => {
                          const monthPct = month.totalLead > 0 ? ((month.totalBooking / month.totalLead) * 100).toFixed(2) : '0.00';
                          const monthKey = `${yearData.year}-${month.monthNum}`;
                          const isMonthExpanded = !!expandedMonths[monthKey];
                          const dayRows = monthDayData[monthKey];
                          return (
                            <React.Fragment key={`${yearData.year}-${month.month}`}>
                              <tr
                                className={`${mIdx % 2 === 0 ? 'bg-white' : 'bg-stone-50'} cursor-pointer hover:bg-blue-50`}
                                onClick={() => toggleMonthExpand(yearData.year, month.monthNum)}
                                data-testid={`trend-month-row-${yearData.year}-${month.monthNum}`}
                              >
                                <td className="px-3 py-2 border pl-8 flex items-center gap-1.5 font-medium">
                                  {isMonthExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                                  {month.month}
                                </td>
                                <td className="px-3 py-2 border text-right">{month.totalLead.toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right">{month.totalOfficeVisit.toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right">{month.totalProjectVisit.toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right">{month.totalScopeOfCustomer.toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right text-emerald-700">{month.totalBooking.toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right">{(month.totalConfirmSale || 0).toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right">{(month.totalRegistry || 0).toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right">{(month.totalPossession || 0).toLocaleString()}</td>
                                <td className="px-3 py-2 border text-right text-slate-600 bg-slate-50">{monthPct}%</td>
                              </tr>
                              {isMonthExpanded && (
                                dayRows === undefined ? (
                                  <tr><td colSpan={10} className="px-3 py-3 text-center text-xs text-slate-400 bg-slate-50">
                                    <RefreshCw className="w-3.5 h-3.5 animate-spin inline-block mr-2" /> Loading day-wise data...
                                  </td></tr>
                                ) : dayRows.length === 0 ? (
                                  <tr><td colSpan={10} className="px-3 py-3 text-center text-xs text-slate-400 bg-slate-50">
                                    No daily data for {month.month} {yearData.year}
                                  </td></tr>
                                ) : dayRows.map((d) => {
                                  const dayPct = d.totalLead > 0 ? ((d.totalBooking / d.totalLead) * 100).toFixed(2) : '0.00';
                                  return (
                                    <tr key={`${monthKey}-${d.day}`} className="bg-slate-50 hover:bg-slate-100" data-testid={`trend-day-row-${monthKey}-${d.day}`}>
                                      <td className="px-3 py-1.5 border pl-14 text-xs text-slate-600">{String(d.day).padStart(2, '0')} {month.month}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{d.totalLead.toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{d.totalOfficeVisit.toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{d.totalProjectVisit.toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{d.totalScopeOfCustomer.toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs text-emerald-700">{d.totalBooking.toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{(d.totalConfirmSale || 0).toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{(d.totalRegistry || 0).toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs">{(d.totalPossession || 0).toLocaleString()}</td>
                                      <td className="px-3 py-1.5 border text-right text-xs text-slate-500 bg-white">{dayPct}%</td>
                                    </tr>
                                  );
                                })
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendTab;
